import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// ---- Auth / identity --------------------------------------------------------
// Every visitor is at least a guest (cookie minted server-side on first call).
// These endpoints let a guest claim a permanent account (email + password)
// or sign back in from any device. `display_name` is what Mochi calls you.
export const fetchMe = () => api.get("/auth/me").then((r) => r.data);
export const unlockAllAccessories = () => api.post("/admin/unlock-all").then((r) => r.data);
export const fetchBypassStatus = () => api.get("/auth/bypass-status").then((r) => r.data);
export const devLogin = () => api.post("/auth/dev-login").then((r) => r.data);
export const setLoginBypass = (enabled) => api.post("/admin/login-bypass", { enabled }).then((r) => r.data);
export const registerAccount = (email, password, display_name) =>
    api.post("/auth/register", { email, password, display_name: display_name || null }).then((r) => r.data);
// Beta signup (used by /beta page) — same endpoint, with an invite code.
export const registerWithCode = (email, password, display_name, invite_code) =>
    api.post("/auth/register", { email, password, display_name: display_name || null, invite_code }).then((r) => r.data);
export const loginAccount = (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data);
export const logoutAccount = () => api.post("/auth/logout").then((r) => r.data);
export const changePassword = (current_password, new_password) =>
    api.post("/auth/change-password", { current_password, new_password }).then((r) => r.data);
// Change the sign-in email. Requires current password.
export const changeEmail = (email, password) =>
    api.post("/auth/email", { email, password }).then((r) => r.data);
// Rename what Mochi calls you.
export const updateDisplayName = (display_name) =>
    api.post("/auth/display-name", { display_name }).then((r) => r.data);
// Forgot password — emails a reset link to the sign-in email.
// Returns { ok, message, email_delivery_configured } regardless of whether
// the email exists (anti-enumeration).
export const forgotPassword = (email) =>
    api.post("/auth/forgot-password", { email }).then((r) => r.data);
// Reset password using the token from the email link. Signs the user in.
export const resetPassword = (token, new_password) =>
    api.post("/auth/reset-password", { token, new_password }).then((r) => r.data);
// Account export — GET returns a JSON dump of every user-scoped record.
export const exportAccount = () => api.get("/account/export").then((r) => r.data);
// Delete account — permanently wipes all user data. Re-auth required.
export const deleteAccount = (password, confirmation) =>
    api.post("/account/delete", { password, confirmation }).then((r) => r.data);

// Tasks
export const fetchTasks = (date) =>
    api.get("/tasks", { params: date ? { date } : {} }).then((r) => r.data);
export const fetchTasksRange = (start, end) =>
    api.get("/tasks", { params: { start, end } }).then((r) => r.data);
export const createTask = (data) => api.post("/tasks", data).then((r) => r.data);
// toggleTask returns { task, win? } — callers can read response.task for the
// updated row, and response.win to decide whether to celebrate.
export const toggleTask = (id, completed) =>
    api.patch(`/tasks/${id}`, { completed }).then((r) => r.data);
// Partial update for a task. Pass any subset of { completed, title, priority }.
export const updateTask = (id, patch) =>
    api.patch(`/tasks/${id}`, patch).then((r) => r.data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`).then((r) => r.data);

// Habits
export const fetchHabits = () => api.get("/habits").then((r) => r.data);
export const createHabit = (data) => api.post("/habits", data).then((r) => r.data);
// toggleHabit returns { habit, win? }
export const toggleHabit = (id) => api.patch(`/habits/${id}/toggle`).then((r) => r.data);
// Partial update for a habit. Pass any subset of { title, schedule_days, time }.
export const updateHabit = (id, patch) => api.patch(`/habits/${id}`, patch).then((r) => r.data);
export const deleteHabit = (id) => api.delete(`/habits/${id}`).then((r) => r.data);
// Mochi's weekly cover pass — protects a habit's streak.
// Returns { habit, settings, win: "cover" }; throws 409 if not eligible.
export const coverHabit = (id) => api.post(`/habits/${id}/cover`).then((r) => r.data);


// Reminders
export const fetchReminders = (date) =>
    api.get("/reminders", { params: date ? { date } : {} }).then((r) => r.data);
export const fetchRemindersRange = (start, end) =>
    api.get("/reminders", { params: { start, end } }).then((r) => r.data);
export const createReminder = (data) => api.post("/reminders", data).then((r) => r.data);
export const toggleReminder = (id, completed) =>
    api.patch(`/reminders/${id}`, { completed }).then((r) => r.data);
// Partial update for a reminder. Pass any subset of { title, time, date, lead_minutes }.
export const updateReminder = (id, patch) =>
    api.patch(`/reminders/${id}`, patch).then((r) => r.data);
export const deleteReminder = (id) => api.delete(`/reminders/${id}`).then((r) => r.data);
// Auto-snooze ladder controls (Mochi-spec).
//   ack   → user tapped notification / banner → cancel future auto-snoozes
//   rest  → user intentionally dismissed → move straight to "rested for today"
//   needsReschedule → list of today's rested-but-undone reminders, fed into
//                     Mochi's "got a little lost today" prompt
export const ackReminder = (id) => api.post(`/reminders/${id}/ack`).then((r) => r.data);
export const restReminder = (id) => api.post(`/reminders/${id}/rest`).then((r) => r.data);
export const fetchRemindersNeedingReschedule = () =>
    api.get("/reminders/needs_reschedule").then((r) => r.data);

// List items (shopping)
export const fetchListItems = () => api.get("/list").then((r) => r.data);
export const createListItem = (data) => api.post("/list", data).then((r) => r.data);
export const toggleListItem = (id, completed) =>
    api.patch(`/list/${id}`, { completed }).then((r) => r.data);
// Partial update for a list item. Pass any subset of { completed, title, quantity }.
export const updateListItem = (id, patch) =>
    api.patch(`/list/${id}`, patch).then((r) => r.data);
export const deleteListItem = (id) => api.delete(`/list/${id}`).then((r) => r.data);

// Settings
export const fetchSettings = () => api.get("/settings").then((r) => r.data);
export const updateSettings = (data) => api.patch("/settings", data).then((r) => r.data);

// Stats
export const fetchStats = () => api.get("/stats").then((r) => r.data);
export const fetchStatsPage = () => api.get("/stats/page").then((r) => r.data);
export const fetchWeeklySummary = () => api.get("/stats/weekly_summary").then((r) => r.data);
export const deliverWeeklySummary = (session_id = "default") =>
    api.post("/stats/weekly_summary/deliver", { message: "", session_id }).then((r) => r.data);

// Avatar message
export const requestAvatarMessage = (event, emotion, context) =>
    api
        .post("/avatar/message", { event, emotion, context })
        .then((r) => r.data);

// Backend clock (Eastern Time)
export const fetchClock = () => api.get("/clock").then((r) => r.data);

// Due reminders (atomically marks them alerted server-side)
export const fetchDueReminders = () => api.get("/reminders/due").then((r) => r.data);

// Global search across all dates/types
export const searchAll = (q) =>
    api.get("/search", { params: { q } }).then((r) => r.data);

// Chat with Mochi
export const fetchChatHistory = (session_id = "default") =>
    api.get("/chat/history", { params: { session_id } }).then((r) => r.data);
export const sendChat = (message, session_id = "default", client_context = null) =>
    api.post("/chat", { message, session_id, client_context }).then((r) => r.data);
export const clearChatHistory = (session_id = "default") =>
    api.delete("/chat/history", { params: { session_id } }).then((r) => r.data);
// Daily chat quota: { limit, used, remaining, exhausted, resets_at }.
// Used by ChatComposer to render "X/10 left today" + disable input at cap.
export const fetchChatQuota = () => api.get("/chat/quota").then((r) => r.data);

// Feed Mochi rice balls. Returns { jar, fed_total, awarded, milestone? }.
// Throws on HTTP 400 when the jar is empty.
export const feedMochi = (count = 1) =>
    api.post("/mochi/feed", { count }).then((r) => r.data);

// Static milestone tier ladder { tiers: [{tier, label}, ...] }
export const fetchMilestones = () =>
    api.get("/mochi/milestones").then((r) => r.data);

// "Mochi this month" engagement card — chats + days active this calendar month
// in Eastern Time. Backend never returns $ or token counts; pure warmth signal.
export const fetchMochiThisMonth = () =>
    api.get("/mochi/this-month").then((r) => r.data);

// Quick-capture (Cmd+K / floating button). Sends one raw string to the
// backend which classifies via Claude AND inserts into the matching
// collection. Returns { type, title, item_id, ambiguous, truncated }.
export const quickCapture = (text) =>
    api.post("/capture", { text }).then((r) => r.data);

// ---- Beta-access admin panel (/admin) --------------------------------------
// All of these hit /api/admin/* which 404s for any non-admin caller, so the
// AdminPanel treats a 404 from adminStats() as "this page doesn't exist".
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminGetConfig = () => api.get("/admin/config").then((r) => r.data);
export const adminSetCap = (beta_user_cap) =>
    api.patch("/admin/config", { beta_user_cap }).then((r) => r.data);
export const adminListCodes = () => api.get("/admin/codes").then((r) => r.data);
export const adminCreateCode = (max_uses, expires_at) =>
    api.post("/admin/codes", { max_uses, expires_at }).then((r) => r.data);
export const adminRevokeCode = (code) =>
    api.post(`/admin/codes/${encodeURIComponent(code)}/revoke`).then((r) => r.data);
export const adminCodeUsers = (code) =>
    api.get(`/admin/codes/${encodeURIComponent(code)}/users`).then((r) => r.data);
export const adminListAllowlist = () => api.get("/admin/allowlist").then((r) => r.data);
export const adminAddAllowlist = (emails) =>
    api.post("/admin/allowlist", { emails }).then((r) => r.data);
export const adminDeleteAllowlist = (email) =>
    api.delete(`/admin/allowlist/${encodeURIComponent(email)}`).then((r) => r.data);
export const adminBetaUsers = (search) =>
    api.get("/admin/beta-users", { params: search ? { search } : {} }).then((r) => r.data);
export const adminToggleBeta = (user_id, is_beta) =>
    api.post(`/admin/beta-users/${encodeURIComponent(user_id)}/toggle-beta`, { is_beta }).then((r) => r.data);
export const adminGrantByEmail = (email) =>
    api.post("/admin/beta-users/grant-by-email", { email }).then((r) => r.data);
// Reset one tester's cosmetics + relationship clock (data untouched).
export const adminResetCosmetics = (user_id) =>
    api.post(`/admin/beta-users/${encodeURIComponent(user_id)}/reset-cosmetics`).then((r) => r.data);
// Member privacy tools — any registered member, not just beta testers.
export const adminLookupMember = (email) =>
    api.get("/admin/members/lookup", { params: { email } }).then((r) => r.data);
export const adminExportMember = (user_id) =>
    api.get(`/admin/members/${encodeURIComponent(user_id)}/export`).then((r) => r.data);
// Irreversible. Requires retyping the member's email; optional farewell email.
export const adminPurgeMember = (user_id, confirm_email, notify) =>
    api.post(`/admin/members/${encodeURIComponent(user_id)}/purge`, { confirm_email, notify }).then((r) => r.data);
export const adminRecentPurges = () => api.get("/admin/members/purges").then((r) => r.data);
// Unlock pace: "normal" ladder vs compressed "beta" ladder for is_beta users.
export const adminGetUnlockPace = () => api.get("/admin/unlock-pace").then((r) => r.data);
export const adminSetUnlockPace = (mode) =>
    api.post("/admin/unlock-pace", { mode }).then((r) => r.data);

// Day-7 wrap-up email + feedback inbox.
export const adminWrapupStatus = () => api.get("/admin/beta-wrapup").then((r) => r.data);
export const adminWrapupPreview = () => api.post("/admin/beta-wrapup/preview").then((r) => r.data);
export const adminListFeedback = () => api.get("/admin/feedback").then((r) => r.data);
export const adminDeleteFeedback = (id) =>
    api.delete(`/admin/feedback/${encodeURIComponent(id)}`).then((r) => r.data);
// "tell Mochi how it went" — any member (or guest) can leave a note.
export const submitFeedback = (message, mood, source) =>
    api.post("/feedback", { message, mood: mood || null, source: source || null }).then((r) => r.data);

export default api;
